import BaseSetup from '@/components/BaseSetup';
import useGoogleMapService from '@/services/GoogleMapService';
import { Reactive } from '@/utils/decorators';
import { debounce } from 'lodash-es';
import { ref } from 'vue';

export type SharedGoogleMapEmits = {};
export type SharedGoogleMapProps = {
	isCover?: boolean
	latitude: number
	longitude: number
};

export default class SharedGoogleMapSetup extends BaseSetup {

	static readonly defaultProps = {};

	readonly _map = ref<HTMLElement>();

	private readonly debouncedStaticMap = debounce((ctx) => {
		ctx.refreshStaticMapAsync();
	}, 1000);

	@Reactive private readonly state = {
		backgroundUrl: '',
		resizeObserver: undefined as Undef<ResizeObserver>,
	};

	get backgroundUrl () { return this.state.backgroundUrl }

	constructor (
		private props: SharedGoogleMapProps,
		private emits: SharedGoogleMapEmits,
	) {
		super();
	}

	protected onMounted () {
		if (typeof window.ResizeObserver !== 'undefined' && this._map.value) {
			this.state.resizeObserver = new ResizeObserver(() => this.triggerStaticMap());
			this.state.resizeObserver.observe(this._map.value);
		}
	}

	protected onBeforeUnmount () {
		if (this._map.value) {
			this.state.resizeObserver?.unobserve(this._map.value);
		}
	}

	private triggerStaticMap () {
		if (!this.state.backgroundUrl) {
			this.refreshStaticMapAsync();
			return;
		}
		this.debouncedStaticMap(this);
	}

	private async refreshStaticMapAsync () {
		if (!this.props.latitude || !this.props.longitude) return;
		if (!this._map.value) return;
		if (!this._map.value.clientHeight || !this._map.value.clientWidth) return;

		let dimensions = [this._map.value.clientWidth, this._map.value.clientHeight];

		if (this.props.isCover) {
			const biggestSideIndex = dimensions[0] > dimensions[1] ? 0 : 1;

			if (dimensions[biggestSideIndex] > 640) {

				const ratio = 640 / dimensions[biggestSideIndex];
				dimensions = dimensions.map(x => Math.round(x * ratio));
			}
		}

		const data = await useGoogleMapService().getAsync({
			latitude: this.props.latitude,
			longitude: this.props.longitude,
			scale: 2,
			zoom: 15,
			width: dimensions[0],
			height: dimensions[1],
			showMarker: true,
		});

		this.state.backgroundUrl = data;
	}

	redirectToGoogleMap () {
		const url = new URL(`https://www.google.com/maps/search/?api=1&query=${this.props.latitude},${this.props.longitude}`);

		url.searchParams.append('query', `${this.props.latitude},${this.props.longitude}`);

		window.open(url.href, 'about:blank');
	}

}
